import React, { useEffect, useState } from "react";
import PropTypes from "kf-proptypes/widgets";
import convert from "react-from-dom";

import { PlaceholderIcon } from "./placeholder.icon.jsx";
import { IconService } from "./service.js";

import styles from "./styles.css";

const ICON_SIZE = {
  SIZE_8: "8-x-8",
  SIZE_12: "12-x-12",
  SIZE_16: "16-x-16",
  SIZE_20: "20-x-20",
  SIZE_24: "24-x-24",
  SIZE_28: "28-x-28",
  SIZE_32: "32-x-32",
  SIZE_40: "40-x-40",
  SIZE_56: "56-x-56"
};

//https://developer.mozilla.org/en-US/docs/Web/CSS/font-weight#common_weight_name_mapping
const ICON_STROKE = {
  THIN: "1-px", //1px
  LIGHT: "1-25-px", //1.25px
  REGULAR: "1-5-px", //1.5px
  MEDIUM: "2-px", //2px
  BOLD: "bold" //bold
};

export const ICON_PADDING = {
  PADDING_NONE: "paddingNone",
  PADDING_4: "padding4",
  PADDING_8: "padding8"
};

const ICON_BACKGROUND = {
  TRANSPARENT: "iconTransparentBg",
  SOLID: "iconSolidBg"
};

const ICON_SHAPE = {
  ROUND: "iconRound",
  SQUARE: "iconSquare"
};

const ICON_HOVER_TYPE = {
  NONE: "transparentHover",
  TRANSPARENT: "transparentHover",
  BORDER: "borderHover",
  SOLID: "solidHover",
  //depreceated will be removed in future relase, and solid
  // will be mapped to lightgrey
  LIGHT_GRAY: "lightGrayHover",
  DARK_GRAY: "darkGrayHover"
};

export const ICON_SERVICE = {
  ICON: IconService.getIcon,
  ILLUSTRATION: IconService.getIllustration
};

function KfIcon(props) {
  return (
    <BaseIcon
      icon={props.icon}
      size={props.size}
      stroke={props.stroke}
      color={props.color}
      service={ICON_SERVICE.ICON}
      className={`${props.className} ${styles.icon}`}
      padding={props.padding}
      backgroundType={props.backgroundType}
      hoverType={props.hoverType}
      onClick={props.onClick}
      iconShape={props.iconShape}
      focused={props.focused}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
    />
  );
}

KfIcon.propTypes = {
  icon: PropTypes.string,
  size: PropTypes.oneOf(Object.values(ICON_SIZE)),
  stroke: PropTypes.oneOf(Object.values(ICON_STROKE)),
  color: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  className: PropTypes.string,
  padding: PropTypes.oneOf(Object.values(ICON_PADDING)),
  backgroundType: PropTypes.string,
  iconShape: PropTypes.string,
  hoverType: PropTypes.string,
  focused: PropTypes.bool,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func
};

KfIcon.defaultProps = {
  size: ICON_SIZE.SIZE_16,
  padding: ICON_PADDING.PADDING_NONE,
  className: "",
  onClick: () => {}
};

export function BaseIconComponent(props) {
  const {
    icon,
    size,
    stroke,
    color,
    onClick = () => {},
    className = "",
    service = ICON_SERVICE.ICON,
    padding,
    backgroundType = ICON_BACKGROUND.TRANSPARENT,
    hoverType = ICON_HOVER_TYPE.TRANSPARENT,
    iconShape = ICON_SHAPE.ROUND,
    focused,
    onMouseEnter,
    onMouseLeave
  } = props;
  const [iconData, setIconData] = useState("");

  useEffect(
    function onIcon() {
      if (icon) {
        service(icon, size, stroke).then((res) => setIconData(res));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [icon, size, stroke]
  );

  // let [sizeValue] = size.split("-x-");

  const style = {
    "--kf-icon-color": color ? `var(${color})` : ""
    // minWidth: `${sizeValue}px`,
    // minHeight: `${sizeValue}px`
  };

  return iconData.length > 0 ? (
    <span
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`${className} 
      ${styles.baseIcon}
      ${styles[padding]} 
      ${styles[iconShape]}
      ${styles[backgroundType]} 
      ${color ? styles.iconColor : ""}
      ${styles[hoverType]}
      ${focused ? styles.focused : ""}`}
      style={style}
      onClick={onClick}
      data-icon={icon}
    >
      {convert(iconData)}
    </span>
  ) : (
    <PlaceholderIcon size={size} className={className} />
  );
}

BaseIconComponent.propTypes = {
  icon: PropTypes.string.isRequired,
  size: PropTypes.oneOf(Object.values(ICON_SIZE)).isRequired,
  stroke: PropTypes.string.isRequired,
  color: PropTypes.string,
  onClick: PropTypes.func,
  className: PropTypes.string,
  service: PropTypes.func,
  padding: PropTypes.string,
  backgroundType: PropTypes.oneOf(Object.values(ICON_BACKGROUND)),
  iconShape: PropTypes.oneOf(Object.values(ICON_SHAPE)),
  hoverType: PropTypes.oneOf(Object.values(ICON_HOVER_TYPE)),
  focused: PropTypes.bool,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func
};

BaseIconComponent.defaultProps = {
  size: ICON_SIZE.SIZE_16,
  padding: ICON_PADDING.PADDING_NONE,
  onClick: () => {},
  focused: false
};

export const BaseIcon = React.memo(BaseIconComponent);

export { preloadIcon } from "./util.js";
export { KF_ICONS, KF_ILLUSTRATIONS } from "./icons/icons.js";
export { KfIllustration, ILLUSTRATION_SIZE } from "./illustrations/index.jsx";
export {
  KfIcon,
  ICON_STROKE,
  ICON_SIZE,
  ICON_BACKGROUND,
  ICON_HOVER_TYPE,
  ICON_SHAPE
};
